export interface MessageImage {
  id: string;
  url: string;
  width?: number;
  height?: number;
}

// --- Emoji Reactions ---

export interface ReactionUser {
  id: number;
  username: string;
}

export interface Reaction {
  emoji: string;              // Unicode char (e.g. "🔥") or custom slug (e.g. ":cool_lotus:")
  count: number;
  users: ReactionUser[];
  reacted: boolean;           // whether the current authenticated user has reacted
  imageUrl?: string;          // CDN URL for custom emoji image
}

export interface CustomEmoji {
  slug: string;
  name: string;
  imageUrl: string;
  uploadedBy: string;
  createdAt: string;
}

export interface CustomEmojiUploadResponse {
  slug: string;
  name: string;
  imageUrl: string;
  remaining: number;
}

export interface MyCustomEmojisResponse {
  emojis: CustomEmoji[];
  activeCount: number;
  maxSlots: number;
}

export interface ReactionUpdatePayload {
  messageId: string;
  conversationId: string;
  reactions: Reaction[];
}

export enum MessageType {
  USER_MESSAGE = 'USER_MESSAGE',
  WANT_LIST_REMINDER = 'WANT_LIST_REMINDER',
  EMAIL_VERIFICATION_REMINDER = 'EMAIL_VERIFICATION_REMINDER',
  PHONE_VERIFICATION_REMINDER = 'PHONE_VERIFICATION_REMINDER',
  TRADE_REMINDER_SHIP = 'TRADE_REMINDER_SHIP',
  TRADE_REMINDER_COMPLETE = 'TRADE_REMINDER_COMPLETE'
}

export interface Message {
  id: string;
  sender: {
    id: number;
    username: string;
    avatarUrl?: string;
    country?: string | null;
    isElite?: boolean;
    showAnimatedBorder?: boolean;
    borderColor1?: string | null;
    borderColor2?: string | null;
  } | null;
  content: string;
  createdAt: string;
  isRead: boolean;
  type?: MessageType;
  isSystemMessage?: boolean;
  images?: MessageImage[];
  reactions?: Reaction[];
}

export interface Conversation {
  conversationId: string;
  otherUser: {
    id: number;
    username: string;
    avatarUrl?: string;
    country?: string | null;
    isElite?: boolean;
    showAnimatedBorder?: boolean;
    borderColor1?: string | null;
    borderColor2?: string | null;
    bubbleColor1?: string | null;
    bubbleColor2?: string | null;
  };
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  isRead: boolean;
}

export interface InboxResponse {
  conversations: Conversation[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface ConversationThread {
  conversationId: string;
  otherUser: {
    id: number;
    username: string;
    avatarUrl?: string;
    country?: string | null;
    isElite?: boolean;
    showAnimatedBorder?: boolean;
    borderColor1?: string | null;
    borderColor2?: string | null;
    bubbleColor1?: string | null;
    bubbleColor2?: string | null;
  };
  messages: Message[];
  imageCount?: number;
}

export type SuggestionEmailFrequency = 'off' | 'daily' | 'realtime';

/**
 * One boolean per email the user can turn off. The nine below the first four
 * replaced the single system-message flag, which the API no longer returns;
 * each is optional here because a backend that predates them omits them, and
 * a missing value means ON.
 */
export interface NotificationSettings {
  emailOnNewMessage: boolean;
  emailOnNewTrade: boolean;
  emailOnNewFeatures: boolean;
  emailOnShipped: boolean;
  emailOnCaseReply?: boolean;
  emailOnCardsAdded?: boolean;
  emailOnTradeFunding?: boolean;
  emailOnTradeReminders?: boolean;
  emailOnOfferReceived?: boolean;
  emailOnOfferResult?: boolean;
  emailOnOfferExpired?: boolean;
  emailOnSetupTips?: boolean;
  emailOnPartnerUpdates?: boolean;
  soundEnabled: boolean;
  /**
   * Trade suggestion email frequency. Its own three-state opt-in (default
   * 'off'), deliberately NOT governed by any of the booleans above: a user
   * setting daily/realtime has affirmatively asked for suggestion email.
   */
  suggestionEmailFrequency: SuggestionEmailFrequency;
}

/** The boolean email toggles of NotificationSettings. */
export type EmailNotificationField = {
  [K in keyof NotificationSettings]-?: K extends `emailOn${string}` ? K : never;
}[keyof NotificationSettings];

// WebSocket event payload interfaces

/**
 * Payload for 'message:new' socket event
 * Emitted to recipient when a new message is sent
 */
export interface NewMessagePayload {
  conversationId: string;
  messageId: string;
  sender: {
    id: number;
    username: string;
    avatarUrl?: string;
    country?: string;
    isElite?: boolean;
    showAnimatedBorder?: boolean;
    borderColor1?: string | null;
    borderColor2?: string | null;
  };
  content: string;
  createdAt: string;
  type: 'USER' | 'SYSTEM';
  images?: MessageImage[];
}

/**
 * Payload for 'message:read' socket event
 * Emitted to sender when recipient reads messages (debounced 500ms)
 */
export interface MessageReadPayload {
  conversationId: string;
  readerId: number;
  readerUsername: string;
  messageIds: string[];
  readAt: string;
}

/**
 * Payload for 'notifications:update' socket event
 * Emitted when badge counts change
 */
export interface NotificationCountsPayload {
  userId: number;
  inboxCount: number;
  receivingCount: number;
  hasOverdueTrades: boolean;
  /** Pending incoming trade offers, for the sidebar Offers badge (additive field) */
  pendingIncomingOffersCount?: number;
}

/**
 * Extended Message interface with read receipt timestamp
 */
export interface MessageWithReadReceipt extends Message {
  readAt?: string;
}
